import styled from "styled-components"

import type { IntelligenceReportData } from "../../pdf/types"
import { DEVICE_INFO_FIELDS, getPriorityColors, getSummaryBullets, SYSTEM_INFO_FIELDS } from "../../sharedTokens"
import { MeasuredPaginator, type PaginatorItem } from "../MeasuredPaginator"
import { BulletList, ContextCard, DetailsCard, HeaderBar, HeaderText, PageCard, PageContent } from "../shared"

const SectionTitle = styled.h2`
  font-size: 14px;
  color: #0f172a;
  margin: 10px 0 8px;
  font-weight: 400;
`

const SummaryCard = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 6px;
  margin: 4px 0 8px;
  border-left: 3px solid #334155;
`

const SummaryCardContent = styled.div`
  flex: 1;
  padding: 8px 0 12px 14px;
`

const SummaryIntro = styled.div`
  font-size: 14px;
  color: #0f172a;
  margin-bottom: 6px;
`

const ContextRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;

  > * {
    flex: 1;
  }
`

interface Props {
  report: IntelligenceReportData
}

export function OverviewPage({ report }: Props) {
  const priorityColors = getPriorityColors(report.priority)
  const summaryBullets = getSummaryBullets(report)
  const hasContext = Boolean(report.device_info || report.system_info)

  const items: PaginatorItem[] = [
    {
      id: "details",
      node: <DetailsCard report={report} priorityColors={priorityColors} />,
    },
    {
      id: "summary",
      node: (
        <>
          <SectionTitle style={{ marginTop: 0 }}>System Summary</SectionTitle>
          <SummaryCard>
            <SummaryCardContent>
              <SummaryIntro>Overall, the system:</SummaryIntro>
              <BulletList items={summaryBullets} />
            </SummaryCardContent>
          </SummaryCard>
        </>
      ),
    },
  ]
  if (hasContext) {
    items.push({
      id: "context",
      node: (
        <>
          <SectionTitle style={{ marginTop: 3 }}>Context</SectionTitle>
          <ContextRow>
            {report.device_info && (
              <ContextCard
                title="Smart Thermostat"
                fields={DEVICE_INFO_FIELDS}
                data={report.device_info as unknown as Record<string, unknown>}
              />
            )}
            {report.system_info && (
              <ContextCard
                title="HVAC System"
                fields={SYSTEM_INFO_FIELDS}
                data={report.system_info as unknown as Record<string, unknown>}
              />
            )}
          </ContextRow>
        </>
      ),
    })
  }

  return (
    <MeasuredPaginator
      items={items}
      renderPage={(nodes, idx) => (
        <PageCard key={idx}>
          <HeaderBar>
            <HeaderText>Overview</HeaderText>
          </HeaderBar>
          <PageContent>{nodes}</PageContent>
        </PageCard>
      )}
    />
  )
}
